import React from 'react';

import { Event } from '@go-shared/types/event';
import { EventElement } from './GridBody';
import { GridScrollPosition } from './Grid';
import { BubbleBadge, BubbleMergedVarHint } from './BubbleUI';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { evaluateMcmTruthyAggregate } from '@go-shared/lib/mcm';
import { BrandCommonTextValues, HourFormat } from '@go-shared/types/brand';
import { generateFormattedTimeString } from '@go-shared/lib/event';
import { evaluateAudienceTruthyAggregate } from '@go-shared/lib/audience';
import { DEFAULT_TIMESTR_FORMAT } from '@go-shared/lib/time';

const DEFAULT_COLOR = '#4d4d4d';

function EVENT_BORDERS(color: string) {
  return {
    borderBottom: `solid thin ${color}`,
    borderRight: `solid thin ${color}`,
  };
}

export interface GridEventStyle {
  top: number;
  left: number;
  width: number;
  height: number;
  backgroundColor?: string;
}

export interface GridEventMeta {
  track: number;
  hourFormat: HourFormat;
}

export interface GridEventPos {
  x: number;
  y: number;
}

export interface GridEventProps {
  hourFormat: HourFormat;
  isReserveEvent?: boolean;
  eventEl: EventElement;
  locked: boolean;
  mcmEndpoints: string[];
  audiences: string[];
  commonTexts: BrandCommonTextValues;
  onMove?: (event: Event, to: GridEventPos, from: GridEventPos) => void;
  onResize?: (event: Event, height: number) => void;
  onDoubleClick?: (event: Event) => void;
  onToggleEventHiddenClick?: (event: Event) => void;
  onIsBusy?: (newState: boolean) => void;
  onStaffMouseEnter: (event: Event, eventStyle: GridEventStyle) => void;
  onStaffMouseLeave: () => void;
  scrollPosition: GridScrollPosition;
}

export interface GridEventState {
  isDragging: boolean;
  isResizing: boolean;
  isMouseOver: boolean;
  mouseEnterDelay?: NodeJS.Timeout;
  onMouseDownScrollPosition: GridScrollPosition;
}

export interface DragOffsets {
  x: number;
  y: number;
}

export default class GridEvent extends React.Component<GridEventProps, GridEventState> {
  public readonly state: GridEventState = {
    isDragging: false,
    isResizing: false,
    isMouseOver: false,
    mouseEnterDelay: undefined,
    onMouseDownScrollPosition: {
      scrollLeft: 0,
      scrollTop: 0,
    },
  };

  private resizeOffsetY: number = 0;
  private offsets: DragOffsets = {
    x: 0,
    y: 0,
  };
  private draggableEventBox: React.RefObject<HTMLDivElement>;

  constructor(props: any) {
    super(props);

    this.handleDoubleClick = this.handleDoubleClick.bind(this);
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleResizeMouseDown = this.handleResizeMouseDown.bind(this);
    this.handleResizeMouseUp = this.handleResizeMouseUp.bind(this);
    this.handleResizeMouseMove = this.handleResizeMouseMove.bind(this);
    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.draggableEventBox = React.createRef<HTMLDivElement>();
  }

  public render() {
    const { eventEl, hourFormat } = this.props;
    const { isDragging, isResizing } = this.state;

    const backgroundColor = eventEl.event.template.segment
      ? eventEl.event.template.segment.color
      : DEFAULT_COLOR;

    const isPricedEvt = eventEl.event.template.priced;
    const isSimpleEvt = eventEl.event.template.simple;
    const hasStaffAllocs = eventEl.event.staffAllocations.length > 0;

    const isVisible =
      !eventEl.event.template.isPrivate &&
      !eventEl.event.template.crewOnly &&
      evaluateMcmTruthyAggregate(
        'visible',
        this.props.mcmEndpoints,
        eventEl.event.mcm,
        eventEl.event.template.mcm
      ) &&
      evaluateAudienceTruthyAggregate(
        'visible',
        this.props.audiences,
        eventEl.event.audience,
        eventEl.event.template.audience
      );
    const isTrending = evaluateMcmTruthyAggregate(
      'trending',
      this.props.mcmEndpoints,
      eventEl.event.mcm,
      eventEl.event.template.mcm
    );

    const showTopStatusPreview = isSimpleEvt || isPricedEvt;
    const showBotStatusPreview = hasStaffAllocs || !isVisible || isTrending !== false;

    let backgroundImage = this.props.isReserveEvent
      ? `url("data:image/svg+xml,` +
        `%3Csvg%20id%3D'stripes-svg'%20xmlns%3D'http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg'%20width%3D'100%25'%20height%3D'100%25'%3E%3Cdefs%3E%3Cpattern%20id%3D'svg-stripes-pattern-7515'%20patternTransform%3D'rotate(45)'%20width%3D'100%25'%20height%3D'4'%20x%3D'0'%20y%3D'0'%20patternUnits%3D'userSpaceOnUse'%3E%3Cg%3E%3Crect%20x%3D'0'%20y%3D'0'%20width%3D'100%25'%20height%3D'2'%20fill%3D'rgba(62%2C%20102%2C%2070%2C%201)'%3E%3C%2Frect%3E%3Crect%20x%3D'0'%20y%3D'2'%20width%3D'100%25'%20height%3D'2'%20fill%3D'rgba(0%2C%20131%2C%20131%2C%201)'%3E%3C%2Frect%3E%3C%2Fg%3E%3C%2Fpattern%3E%3C%2Fdefs%3E%3Crect%20x%3D'0'%20y%3D'0'%20width%3D'100%25'%20height%3D'100%25'%20fill%3D'url(%23svg-stripes-pattern-7515)'%3E%3C%2Frect%3E%3C%2Fsvg%3E` +
        `%0A")`
      : undefined;

    return (
      <div
        ref={this.draggableEventBox}
        className="absolute select-none shadow overflow-hidden"
        onDoubleClick={this.handleDoubleClick}
        onMouseEnter={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
        style={{
          ...EVENT_BORDERS('white'),
          backgroundImage,
          ...eventEl.style,
          backgroundColor,
          opacity: 1,
          zIndex: isDragging || isResizing ? 20 : 10,
        }}
      >
        <div
          className="w-full h-full overflow-hidden text-white"
          style={{ padding: '4px 4px' }}
          onMouseDown={this.handleMouseDown}
        >
          <div className="text-white font-bold" style={{ fontSize: 10, lineHeight: '10px' }}>
            {generateFormattedTimeString(
              eventEl.event.startTime,
              eventEl.event.duration,
              hourFormat,
              DEFAULT_TIMESTR_FORMAT,
              this.props.commonTexts,
              eventEl.event.interactiveFeedDisplay,
              eventEl.event.template.interactiveFeedDisplay,
              undefined
            )}
          </div>
          <div className="text-white text-xs leading-4">
            <BubbleMergedVarHint
              text={eventEl.event.template.title}
              mergedVars={eventEl.event.mergedVars}
              textOnly
            />
          </div>
          {showTopStatusPreview && (
            <div
              className="absolute top-0 right-0 whitespace-nowrap flex justify-center
            not-italic max-w-full overflow-hidden pointer-events-none border-b border-l border-white"
              style={{ height: 15 }}
            >
              {isSimpleEvt && (
                <BubbleBadge
                  variant="ocean"
                  style={{ width: 15, height: 15 }}
                  className="p-0-i grid items-center justify-center text-center rounded-0-i"
                >
                  <FontAwesomeIcon icon="s" />
                </BubbleBadge>
              )}
              {isPricedEvt && (
                <BubbleBadge
                  variant="lime"
                  style={{ width: 15, height: 15 }}
                  className="p-0-i grid items-center justify-center text-center rounded-0-i"
                >
                  <FontAwesomeIcon icon="dollar-sign" />
                </BubbleBadge>
              )}
            </div>
          )}
          {showBotStatusPreview && (
            <div
              className="absolute bottom-0 right-0 whitespace-nowrap flex justify-center
            not-italic max-w-full overflow-hidden pointer-events-none border-t border-l border-white"
              style={{ height: 15 }}
            >
              {hasStaffAllocs && (
                <BubbleBadge
                  variant="slate"
                  style={{ width: 15, height: 15 }}
                  className="p-0-i grid items-center justify-center text-center rounded-0-i"
                >
                  <FontAwesomeIcon icon="user" />
                </BubbleBadge>
              )}
              {isTrending !== false && (
                <BubbleBadge
                  variant={isTrending ? 'crimson' : 'banana'}
                  style={{ width: 15, height: 15 }}
                  className="p-0-i grid items-center justify-center text-center rounded-0-i"
                >
                  <FontAwesomeIcon icon="fire" />
                </BubbleBadge>
              )}
              {!isVisible && (
                <BubbleBadge
                  variant={isVisible === null ? 'banana' : 'smoke'}
                  style={{ width: 15, height: 15 }}
                  className="p-0-i grid items-center justify-center text-center rounded-0-i"
                >
                  <FontAwesomeIcon icon={isVisible === null ? 'eye' : 'eye-slash'} />
                </BubbleBadge>
              )}
            </div>
          )}
        </div>
        <div
          className="w-full absolute"
          onMouseDown={this.handleResizeMouseDown}
          style={{ height: 10, bottom: -5, cursor: 'ns-resize' }}
        />
      </div>
    );
  }

  public override componentDidUpdate(
    _: Readonly<GridEventProps>,
    prevState: Readonly<GridEventState>
  ): void {
    const { onIsBusy } = this.props;
    if (
      onIsBusy &&
      (prevState.isDragging !== this.state.isDragging ||
        prevState.isResizing !== this.state.isResizing)
    ) {
      onIsBusy(this.state.isDragging || this.state.isResizing);
    }
  }

  private handleMouseDown(e: React.MouseEvent<HTMLDivElement>) {
    if (this.props.locked || e.button !== 0) return;
    this.setState({
      isDragging: true,
      onMouseDownScrollPosition: this.props.scrollPosition,
    });

    const box = this.draggableEventBox.current;
    if (box) {
      this.offsets = {
        x: box.offsetLeft - e.clientX,
        y: box.offsetTop - e.clientY,
      };
    }

    document.addEventListener('mousemove', this.handleMouseMove);
    document.addEventListener('mouseup', this.handleMouseUp);
  }

  private handleMouseUp() {
    if (!this.state.isDragging) return;

    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);

    this.setState({ isDragging: false });

    const box = this.draggableEventBox.current;
    const { onMove, eventEl } = this.props;
    if (onMove && box) {
      const to = { x: box.offsetLeft, y: box.offsetTop };
      const from = { x: eventEl.style.left, y: eventEl.style.top };

      if (to.x === from.x && to.y === from.y) {
        // do not trigger onMove if the position is exactly the same
        return;
      }
      onMove(eventEl.event, to, from);
    }
  }

  private setDraggableBoxSize(newOffset: number) {
    if (this.draggableEventBox.current) {
      const box = this.draggableEventBox.current;
      box.style.height = newOffset + 'px';
      this.resizeOffsetY = newOffset;
    }
  }

  private setBoxCords(x: number, y: number) {
    const box = this.draggableEventBox.current;
    if (box) {
      box.style.top = y + 'px';
      box.style.left = x + 'px';
    }
  }

  private handleMouseMove(e: MouseEvent) {
    if (!this.state.isDragging) return;

    const { scrollPosition } = this.props;
    const { onMouseDownScrollPosition } = this.state;
    const cords: DragOffsets = {
      x: e.clientX + scrollPosition.scrollLeft - onMouseDownScrollPosition.scrollLeft,
      y: e.clientY + scrollPosition.scrollTop - onMouseDownScrollPosition.scrollTop,
    };
    this.setBoxCords(cords.x + this.offsets.x, cords.y + this.offsets.y);
  }

  private handleResizeMouseDown(e: React.MouseEvent<HTMLDivElement>) {
    if (this.props.locked || e.button !== 0) return;
    this.setState({
      isResizing: true,
    });

    if (this.draggableEventBox.current) {
      const resizeResult = e.clientY - this.draggableEventBox.current.getBoundingClientRect().top;
      this.setDraggableBoxSize(resizeResult);
    }

    document.addEventListener('mousemove', this.handleResizeMouseMove);
    document.addEventListener('mouseup', this.handleResizeMouseUp);
  }

  private handleResizeMouseUp() {
    if (!this.state.isResizing) return;

    document.removeEventListener('mousemove', this.handleResizeMouseMove);
    document.removeEventListener('mouseup', this.handleResizeMouseUp);

    this.setDraggableBoxSize(this.resizeOffsetY);
    this.setState({ isResizing: false });

    if (this.props.onResize && this.draggableEventBox.current) {
      this.props.onResize(this.props.eventEl.event, this.draggableEventBox.current.offsetHeight);
    }
  }

  private handleResizeMouseMove(e: MouseEvent) {
    if (!this.state.isResizing) return;
    if (this.draggableEventBox.current) {
      const resizeResult = e.clientY - this.draggableEventBox.current.getBoundingClientRect().top;
      this.setDraggableBoxSize(resizeResult);
    }
  }

  private handleDoubleClick(e: React.MouseEvent<HTMLDivElement>) {
    if (this.props.onDoubleClick && !this.props.locked) {
      this.props.onDoubleClick(this.props.eventEl.event);
    }
    e.stopPropagation();
  }

  private handleMouseEnter() {
    // small delay to make sure the user intends to open the popup
    const mouseEnterDelay = setTimeout(() => {
      this.props.onStaffMouseEnter(this.props.eventEl.event, this.props.eventEl.style);
      this.setState({ isMouseOver: true });
    }, 400) as unknown as NodeJS.Timeout;
    this.setState({ mouseEnterDelay });
  }

  private handleMouseLeave() {
    if (this.state.mouseEnterDelay) {
      clearTimeout(this.state.mouseEnterDelay);
    }
    this.props.onStaffMouseLeave();
    this.setState({ isMouseOver: false });
  }
}
